import React, { useEffect, useMemo, useState } from "react";
import { PlusCircle, Save } from "lucide-react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { formatDateTime, getStatusLabel, getStatusTone } from "../lib/utils";

const initialForm = {
  registration: "",
  type: "",
  capacity: 8,
  assigned_driver_id: "",
  status: "available",
};

export default function VehiclesPage() {
  const { vehicles, users, loadVehicles, loadUsers, createVehicle, updateVehicle } = useData();
  const [form, setForm] = useState(initialForm);
  const [editingVehicle, setEditingVehicle] = useState(null);

  useEffect(() => {
    loadVehicles();
    loadUsers("driver");
  }, [loadVehicles, loadUsers]);

  const drivers = useMemo(() => users.filter((entry) => entry.role === "driver"), [users]);

  const resetForm = () => {
    setForm(initialForm);
    setEditingVehicle(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      capacity: Number(form.capacity),
      assigned_driver_id: form.assigned_driver_id || null,
    };
    const result = editingVehicle ? await updateVehicle(editingVehicle.id, payload) : await createVehicle(payload);
    if (result.success) {
      toast.success(editingVehicle ? "Vehicle updated." : "Vehicle created.");
      resetForm();
      loadVehicles();
    } else {
      toast.error(result.error);
    }
  };

  const handleEdit = (vehicle) => {
    setEditingVehicle(vehicle);
    setForm({
      registration: vehicle.registration,
      type: vehicle.type,
      capacity: vehicle.capacity,
      assigned_driver_id: vehicle.assigned_driver_id || "",
      status: vehicle.status,
    });
  };

  return (
    <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr] animate-fade-in">
      <form onSubmit={handleSubmit} className="guardian-card bg-white p-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">{editingVehicle ? "Edit vehicle" : "Add vehicle"}</h2>
            <p className="mt-1 text-sm text-[#64748B]">Maintain fleet registration, capacity, and driver assignments.</p>
          </div>
          {editingVehicle ? (
            <button type="button" onClick={resetForm} className="text-sm font-medium text-[#163D6B]">Cancel</button>
          ) : null}
        </div>
        <div className="mt-6 grid gap-4">
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Registration</label>
            <input value={form.registration} onChange={(event) => setForm((current) => ({ ...current, registration: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Vehicle type</label>
            <input value={form.type} onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Capacity</label>
            <input type="number" min="1" value={form.capacity} onChange={(event) => setForm((current) => ({ ...current, capacity: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Assigned driver</label>
            <select value={form.assigned_driver_id} onChange={(event) => setForm((current) => ({ ...current, assigned_driver_id: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
              <option value="">Assign later</option>
              {drivers.map((driver) => (
                <option key={driver.id} value={driver.id}>{driver.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-sm font-medium text-[#0F172A]">Status</label>
            <select value={form.status} onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))} className="mt-2 w-full rounded-xl border border-[#E2E8F0] px-3 py-3 text-sm">
              <option value="available">Available</option>
              <option value="in_use">In use</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>
        <button type="submit" className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#163D6B] px-4 py-3 text-sm font-semibold text-white">
          {editingVehicle ? <Save size={16} /> : <PlusCircle size={16} />}
          {editingVehicle ? "Save changes" : "Add vehicle"}
        </button>
      </form>

      <div className="guardian-card overflow-hidden bg-white">
        <div className="border-b border-[#E2E8F0] px-6 py-4">
          <h2 className="font-['Manrope'] text-xl font-semibold text-[#0F172A]">Fleet register</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[#64748B]">
              <tr>
                <th className="px-6 py-3 font-medium">Registration</th>
                <th className="px-6 py-3 font-medium">Type</th>
                <th className="px-6 py-3 font-medium">Capacity</th>
                <th className="px-6 py-3 font-medium">Status</th>
                <th className="px-6 py-3 font-medium">Updated</th>
                <th className="px-6 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {vehicles.length ? (
                vehicles.map((vehicle) => (
                  <tr key={vehicle.id} className="border-t border-[#E2E8F0]">
                    <td className="px-6 py-4 font-medium text-[#0F172A]">{vehicle.registration}</td>
                    <td className="px-6 py-4 text-[#475569]">{vehicle.type}</td>
                    <td className="px-6 py-4 text-[#475569]">{vehicle.capacity}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold ${getStatusTone(vehicle.status)}`}>
                        {getStatusLabel(vehicle.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#475569]">{formatDateTime(vehicle.updated_at || vehicle.created_at)}</td>
                    <td className="px-6 py-4">
                      <button onClick={() => handleEdit(vehicle)} className="rounded-lg border border-[#E2E8F0] px-3 py-2 text-xs font-medium text-[#0F172A]">
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-[#64748B]">No vehicles found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
